import type {
  AiVerificationPlanStrategy,
  AiVerificationTemplate,
} from '@citizen-walk/shared-types';

/**
 * 検証テンプレートの「配分デフォルト」(defaultDistribution / defaultRandomConstraints /
 * defaultCandidateCriteria / defaultEndAtOffsetDays) を、配分セット新規作成フォームと配分の予約
 * フォームの初期値に変換する pure 関数 (2026-09-04、docs/98 R-82 解消)。
 *
 * テンプレート編集画面 (DefaultDistributionSection) は以前から「テンプレを選んだ時点で自動入力されます」と
 * 案内していたが、消費側が無かった。本関数がその消費側で、**テンプレートを選び直すたびに上書き**する
 * (案内文どおり。地区側は入力後に自由に変えられる)。
 *
 * 対応表:
 *   - defaultDistribution: random_per_user → random_per_user / all_same → all_users / manual → round_robin。
 *     free は配分セットに対応する配り方が無い (自由検証専用) ため配り方は変えず、注記だけ返す。
 *   - defaultRandomConstraints: minPostsPerUser (legacy 名、実体は上限) → maxPerUser、minOwnPosts /
 *     preferOwnPosts → セルフ検証設定 (どちらか有効なら「自分の投稿を除外」はオフにする)。
 *   - defaultCandidateCriteria: session_only → 参加セッションの投稿だけを配る (sameSessionOnly=true)、
 *     set_session なら「対象セッションを選んでください」の注記。all_ai_done → sameSessionOnly=false。
 *     manual_pick → 変更なし。
 *   - defaultEndAtOffsetDays → 回答期限 (配分日から N 日)。
 */

export type TemplateDefaultsSource = Pick<
  AiVerificationTemplate,
  | 'defaultDistribution'
  | 'defaultRandomConstraints'
  | 'defaultCandidateCriteria'
  | 'defaultEndAtOffsetDays'
>;

export interface TemplateDefaultsPatch {
  strategy?: AiVerificationPlanStrategy;
  maxPerUser?: number;
  excludeOwnPosts?: boolean;
  minOwnPosts?: number;
  preferOwnPosts?: boolean;
  sameSessionOnly?: boolean;
  endAtOffsetDays?: number;
  /** フォームに出す補足 (自由検証向け、対象セッションを選ぶ促し等) */
  notes: string[];
  /** 4 項目のいずれかがテンプレートに設定されていたか (false なら何も反映しない) */
  hasAny: boolean;
}

export function templateDefaultsToPatch(
  t: TemplateDefaultsSource | null | undefined,
): TemplateDefaultsPatch {
  const patch: TemplateDefaultsPatch = { notes: [], hasAny: false };
  if (!t) return patch;

  if (t.defaultDistribution) {
    patch.hasAny = true;
    switch (t.defaultDistribution) {
      case 'random_per_user':
        patch.strategy = 'random_per_user';
        break;
      case 'all_same':
        patch.strategy = 'all_users';
        break;
      case 'manual':
        patch.strategy = 'round_robin';
        break;
      case 'free':
        patch.notes.push(
          'このテンプレートは自由検証（参加者が自分で投稿を選ぶ）向けの設定です。配り方はそのままにしました。',
        );
        break;
    }
  }

  const rc = t.defaultRandomConstraints;
  if (rc) {
    patch.hasAny = true;
    if (Number.isFinite(rc.minPostsPerUser) && rc.minPostsPerUser > 0) {
      patch.maxPerUser = Math.floor(rc.minPostsPerUser);
    }
    const minOwn =
      Number.isFinite(rc.minOwnPosts) && rc.minOwnPosts > 0 ? Math.floor(rc.minOwnPosts) : 0;
    patch.minOwnPosts = minOwn;
    patch.preferOwnPosts = rc.preferOwnPosts === true;
    if (minOwn > 0 || rc.preferOwnPosts) patch.excludeOwnPosts = false;
  }

  const cc = t.defaultCandidateCriteria;
  if (cc) {
    patch.hasAny = true;
    if (cc.type === 'session_only') {
      patch.sameSessionOnly = true;
      if (cc.sessionScope === 'set_session') {
        patch.notes.push('テンプレートの推奨: 対象セッションを選んでください。');
      }
    } else if (cc.type === 'all_ai_done') {
      patch.sameSessionOnly = false;
    }
  }

  if (typeof t.defaultEndAtOffsetDays === 'number' && t.defaultEndAtOffsetDays >= 0) {
    patch.hasAny = true;
    patch.endAtOffsetDays = Math.floor(t.defaultEndAtOffsetDays);
  }
  return patch;
}
